import React from 'react';
import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://avanshyadav.netlify.app';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;

/**
 * SEO Meta component — renders all head tags required for ranking.
 * Usage: <Meta title="..." description="..." canonicalPath="/" schema={[...]} />
 */
const Meta = ({
  title,
  description,
  keywords,
  author,
  canonicalPath,
  ogTitle,
  ogDescription,
  ogImage,
  ogType,
  twitterCard,
  twitterTitle,
  twitterDescription,
  twitterImage,
  noIndex,
  schema,
}) => {
  const canonical = `${BASE_URL}${canonicalPath || '/'}`;
  const resolvedOgImage = ogImage || DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      {/* ── Standard SEO ─────────────────────────────────── */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content={author || 'Avansh Yadav'} />
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={
          noIndex
            ? 'noindex, nofollow'
            : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
        }
      />

      {/* ── Open Graph ───────────────────────────────────── */}
      <meta property="og:type" content={ogType || 'website'} />
      <meta property="og:site_name" content="Avansh Yadav Portfolio" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:image" content={resolvedOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:image:alt"
        content={`${ogTitle || title} - Avansh Yadav Portfolio`}
      />
      <meta property="og:locale" content="en_US" />

      {/* ── Twitter Card ─────────────────────────────────── */}
      <meta name="twitter:card" content={twitterCard || 'summary_large_image'} />
      <meta name="twitter:site" content="@Avansh2006" />
      <meta name="twitter:creator" content="@Avansh2006" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={twitterTitle || ogTitle || title} />
      <meta
        name="twitter:description"
        content={twitterDescription || ogDescription || description}
      />
      <meta name="twitter:image" content={twitterImage || resolvedOgImage} />
      <meta
        name="twitter:image:alt"
        content={`${ogTitle || title} - Avansh Yadav Portfolio`}
      />

      {/* ── JSON-LD Structured Data ───────────────────────── */}
      {schema &&
        schema.map((s, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(s)}
          </script>
        ))}
    </Helmet>
  );
};

/* ── Per-page preset configurations ─────────────────────────── */

export const HOME_META = {
  title: 'Avansh Yadav | Full Stack & GenAI Developer | MERN Stack Portfolio',
  description:
    'Avansh Yadav is a Full Stack Developer and GenAI Engineer specializing in MERN Stack, React, Node.js, and AI-powered web applications. View projects and get in touch.',
  keywords:
    'Avansh Yadav, Full Stack Developer, GenAI Engineer, MERN Stack Developer, React Developer, Software Engineer, Web Developer Portfolio, AI Engineer, Node.js, MongoDB, Army Institute of Technology Pune',
  canonicalPath: '/',
  schema: [
    {
      '@context': 'https://schema.org',
      '@type': 'Person',
      '@id': `${BASE_URL}/#person`,
      name: 'Avansh Yadav',
      givenName: 'Avansh',
      familyName: 'Yadav',
      jobTitle: 'Full Stack Developer & GenAI Engineer',
      description:
        'Full Stack Developer and GenAI Engineer specializing in MERN Stack, React, Node.js, and AI-powered web applications.',
      url: `${BASE_URL}/`,
      image: `${BASE_URL}/Photo.jpeg`,
      alumniOf: {
        '@type': 'CollegeOrUniversity',
        name: 'Army Institute of Technology',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Pune',
          addressCountry: 'IN',
        },
      },
      sameAs: [
        'https://github.com/Avansh2006',
        'https://www.linkedin.com/in/avanshyadav/',
        'https://www.instagram.com/yadavav2006',
        'https://twitter.com/Avansh2006',
      ],
      knowsAbout: [
        'Full Stack Development',
        'MERN Stack',
        'React',
        'Node.js',
        'Generative AI',
        'LangChain',
        'TensorFlow',
        'MongoDB',
        'Docker',
        'AWS',
        'Machine Learning',
        'REST APIs',
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${BASE_URL}/#website`,
      name: 'Avansh Yadav Portfolio',
      url: `${BASE_URL}/`,
      description:
        'Portfolio of Avansh Yadav — Full Stack Developer and GenAI Engineer.',
      author: { '@id': `${BASE_URL}/#person` },
      inLanguage: 'en-US',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${BASE_URL}/projects?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'Who is Avansh Yadav?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Avansh Yadav is an Automation and Robotics Engineering student at the Army Institute of Technology, Pune, and a Full Stack and GenAI Developer. He builds intelligent web platforms using React, Node.js, Three.js, and integrating large language models.'
          }
        },
        {
          '@type': 'Question',
          'name': 'What are Avansh Yadav\'s primary programming skills?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Avansh is skilled in Javascript, Java, HTML, CSS, React, Three.js, GSAP, Node.js, Express, Firebase, MySQL, and OpenAI APIs.'
          }
        },
        {
          '@type': 'Question',
          'name': 'What notable projects has Avansh Yadav built?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Avansh built the Smart India Hackathon 2024 AI Article summarizer prototype and the Smart Wash Tech real-time campus laundry reservation dashboard.'
          }
        }
      ]
    },
  ],
};

export const PROJECTS_META = {
  title: 'Projects | Avansh Yadav — Full Stack & AI Portfolio',
  description:
    'Browse all projects by Avansh Yadav including MERN Stack web apps, Generative AI systems, RAG pipelines, and cloud-deployed solutions.',
  keywords:
    'Avansh Yadav projects, Full Stack projects, MERN stack projects, AI projects, React projects, Node.js projects, portfolio projects, GenAI projects',
  canonicalPath: '/projects',
  schema: [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Projects — Avansh Yadav Portfolio',
      description:
        'A curated collection of Full Stack and AI/ML projects built by Avansh Yadav.',
      url: `${BASE_URL}/projects`,
      author: { '@id': `${BASE_URL}/#person` },
      inLanguage: 'en-US',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: `${BASE_URL}/`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Projects',
          item: `${BASE_URL}/projects`,
        },
      ],
    },
  ],
};

export const getProjectMeta = (project) => ({
  title: project?.title
    ? `${project.title} | Avansh Yadav Project`
    : 'Project | Avansh Yadav Portfolio',
  description:
    project?.description ||
    'A full-stack or AI project by Avansh Yadav, showcasing modern web development techniques.',
  keywords: project?.tags
    ? `${project.tags.join(', ')}, Avansh Yadav, portfolio project`
    : 'Avansh Yadav, portfolio project',
  canonicalPath: `/projects`,
  schema: project
    ? [
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: project.title,
          description: project.description,
          author: { '@id': `${BASE_URL}/#person` },
          applicationCategory: 'WebApplication',
          url: project.live || `${BASE_URL}/project/${project.id}`,
          ...(project.github && project.github !== 'Private'
            ? { codeRepository: project.github }
            : {}),
          keywords: project.tags ? project.tags.join(', ') : '',
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: `${BASE_URL}/`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Projects',
              item: `${BASE_URL}/projects`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: project.title,
              item: `${BASE_URL}/project/${project.id}`,
            },
          ],
        },
      ]
    : [],
});

export default Meta;
